import React from "react";

import { useMorphModifier } from "@agent-ui/features/modifiers/hooks/useMorphModifier";
import {
  useDisplayMode,
  useIsDeleting,
  useModifiersStoreActions,
  useSemanticElement,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import isEqual from "lodash-es/isEqual";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

interface ReactEditorGlobalHotkeysListenerProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

const ReactEditorGlobalHotkeysListener: React.FC<
  ReactEditorGlobalHotkeysListenerProps
> = ({ containerRef }) => {
  const displayMode = useDisplayMode();
  const semanticElement = useSemanticElement();
  const isDeleting = useIsDeleting();
  const modifierActions = useModifiersStoreActions();

  const { deleteElement, isProcessing: isPending } = useMorphModifier();

  const handleDelete = React.useCallback(() => {
    const canDeleteElement =
      displayMode === "select" &&
      !isPending &&
      !isDeleting &&
      semanticElement?.elementType !== "text" &&
      semanticElement;

    if (canDeleteElement) {
      // NOTE (Nick, 2025-12-11): Set isDeleting in store so the Modifiers panel
      // shows the disabled state and prevents spam clicks
      modifierActions.setIsDeleting(true);
      deleteElement();
    }
  }, [
    displayMode,
    semanticElement,
    isPending,
    isDeleting,
    modifierActions,
    deleteElement,
  ]);

  useReploHotkeys(
    {
      delete: handleDelete,
    },
    containerRef.current,
  );

  // Note (Noah, 2025-09-18): Whenever the selected element changes, put focus on the
  // editor container so that keyboard events work
  useOnValueChange(
    semanticElement,
    (newElement, oldElement) => {
      if (
        oldElement?.selector !== newElement?.selector &&
        newElement &&
        containerRef.current
      ) {
        containerRef.current.focus();
      }
    },
    { equalityFn: isEqual },
  );

  return null;
};

export default ReactEditorGlobalHotkeysListener;
